import React, { useState, useMemo, useEffect, useContext, useRef, useCallback } from "react";
import { ThemeContext } from "../../../../context/ThemeContext";
import { getData } from "../../../../services/Fetchnodeservices";
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default markers
if (typeof window !== 'undefined') {
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  });
}

const OSM_TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const OSM_TILE_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

// ═════════════════════════════════════════════════════════════════════════════
// ICONS
// ═════════════════════════════════════════════════════════════════════════════
const MapPinIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const MinusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M23 4v6h-6" />
    <path d="M1 20v-6h6" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
    <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const RouteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="6" cy="19" r="3" />
    <path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15" />
    <circle cx="18" cy="5" r="3" />
  </svg>
);

const ReverseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 2v6h6" />
    <path d="M21 12A9 9 0 0 0 6 5.3L3 8" />
    <path d="M21 22v-6h-6" />
    <path d="M3 12a9 9 0 0 0 15 6.7l3-2.7" />
  </svg>
);

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const XIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const GlobeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

// ═════════════════════════════════════════════════════════════════════════════
// MAPPING DASHBOARD - OpenStreetMap (Leaflet) powered
// ═════════════════════════════════════════════════════════════════════════════

const MappingDashboard = () => {
  const { theme } = useContext(ThemeContext);

  const mockLocations = [
    { id: 1, type: "restaurant", name: "Gwalior Restaurant", lat: 26.2183, lng: 75.8267, address: "Gwalior, Madhya Pradesh" },
    { id: 2, type: "restaurant", name: "New Delhi Dhaba", lat: 28.7041, lng: 77.1025, address: "New Delhi" },
    { id: 3, type: "restaurant", name: "Lucknow Cuisine", lat: 26.8467, lng: 80.9462, address: "Lucknow, Uttar Pradesh" },
    { id: 4, type: "address", name: "Hazratganj, Connaught Place", lat: 28.6292, lng: 77.2197, address: "New Delhi, Delhi" },
    { id: 5, type: "pickup", name: "Order Pickup Point", lat: 26.2389, lng: 75.8659, address: "Gwalior Central" },
    { id: 6, type: "drop", name: "Order Drop Point", lat: 28.5921, lng: 77.2064, address: "Delhi South" },
    { id: 7, type: "restaurant", name: "Amritsar Corner", lat: 31.6340, lng: 74.8723, address: "Amritsar, Punjab" },
    { id: 8, type: "address", name: "CP Delhi", lat: 28.6305, lng: 77.2245, address: "Connaught Place, Delhi" },
    { id: 9, type: "restaurant", name: "Jaipur Royal", lat: 26.9124, lng: 75.7873, address: "Jaipur, Rajasthan" },
  ];

  const [locations, setLocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedState, setSelectedState] = useState("all");
  const [availableCities, setAvailableCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(true);
  const [mapCenter, setMapCenter] = useState({ lat: 26.2183, lng: 78.1828 });
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [activeTab, setActiveTab] = useState("search");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [searchProvider, setSearchProvider] = useState(null);
  const searchControllerRef = useRef(null);
  const leafletMapRef = useRef(null);
  const leafletMapInstance = useRef(null);
  const leafletTileRef = useRef(null);
  const leafletMarkersRef = useRef([]);
  const leafletPolygonsRef = useRef([]);

  // Load cities dynamically
  useEffect(() => {
    let cancelled = false;
    async function loadCities() {
      try {
        setCitiesLoading(true);
        const res = await getData("admin/settings/regions-cities");
        if (!cancelled && res && res.status && Array.isArray(res.data)) {
          setAvailableCities(res.data.map(c => c.city).filter(Boolean));
        }
      } catch (e) {
        console.warn("Failed to load cities:", e);
      } finally {
        if (!cancelled) setCitiesLoading(false);
      }
    }
    loadCities();
    return () => { cancelled = true; };
  }, []);

  // Load zones on mount and when city changes
  useEffect(() => {
    let cancelled = false;
    async function loadZones() {
      try {
        const city = (selectedState && selectedState !== "all") ? selectedState : "";
        const params = new URLSearchParams();
        if (city) params.set("city", city);
        const res = await getData(`admin/settings/regions?${params.toString()}`);
        if (!cancelled && res && res.status && Array.isArray(res.data)) {
          const mapped = res.data.map(row => {
            const polygon = row.polygon && Array.isArray(row.polygon) && row.polygon.length
              ? row.polygon
              : null;
            return {
              id: row.id,
              name: row.name,
              city: row.city || inferCityValue(row.name),
              type: row.zone_type === "blacklisted" ? "blacklisted" : "allowed",
              status: row.status,
              hide: row.hide,
              address: polygon ? `${row.city || inferCityValue(row.name)} - ${polygon.length} pts` : (row.city || inferCityValue(row.name)),
              polygon,
              lat: polygon && polygon.length ? polygon[0].lat : 26.2183,
              lng: polygon && polygon.length ? polygon[0].lng : 78.1828,
            };
          });
          setLocations(mapped);
        }
      } catch (e) {
        console.warn("Zone load failed:", e);
      }
    }
    loadZones();
    return () => { cancelled = true; };
  }, [selectedState]);

  function inferCityValue(name) {
    const value = String(name || "").trim();
    const tagged = value.match(/^\[([^\]]+)\]\s*/);
    if (tagged) return tagged[1].trim();
    return (value.split(",")[0] || "").trim() || null;
  }

  // Backend search with debounce and cancellation
  const searchLocations = useCallback(async (query) => {
    if (searchControllerRef.current) {
      searchControllerRef.current.abort();
    }
    const controller = new AbortController();
    searchControllerRef.current = controller;

    if (!query || String(query).trim().length < 2) {
      setLocations([]);
      setSearchLoading(false);
      setSearchError(null);
      setSearchProvider(null);
      return;
    }

    setSearchLoading(true);
    setSearchError(null);
    setSearchProvider(null);

    try {
      const params = new URLSearchParams();
      params.set("q", String(query).trim());
      params.set("limit", "12");
      const res = await getData(`admin/settings/regions/search?${params.toString()}`);
      if (controller.signal.aborted) return;

      if (res && res.status && Array.isArray(res.data)) {
        const mapped = res.data.map(item => ({
          id: item.place_id || `${item.source || "result"}-${item.lat}-${item.lng}`,
          name: item.name,
          city: item.address?.city || inferCityValue(item.display_name || item.name),
          type: item.type || "place",
          status: "1",
          hide: "0",
          address: item.display_name || item.display || `${item.lat.toFixed(4)}, ${item.lng.toFixed(4)}`,
          polygon: item.polygon && Array.isArray(item.polygon) && item.polygon.length ? item.polygon : null,
          geojson: item.geojson || null,
          lat: item.lat,
          lng: item.lng,
          source: item.source,
          provider: res.provider || null,
          raw: item,
        }));
        setLocations(mapped);
        setSearchProvider(res.provider || null);
      } else if (res && res.status === false) {
        setSearchError(res.message || "Search failed");
        setLocations([]);
      }
    } catch (e) {
      if (controller.signal.aborted) return;
      console.warn("Search error:", e);
      setSearchError(e.message || "Search failed");
      setLocations([]);
    } finally {
      if (!controller.signal.aborted) {
        setSearchLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchLocations(searchTerm);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm, searchLocations]);

  // Filter locations
  const filteredLocations = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return locations.filter(loc => {
      const matchesSearch =
        !q ||
        (loc.name && loc.name.toLowerCase().includes(q)) ||
        (loc.address && loc.address.toLowerCase().includes(q)) ||
        (loc.city && loc.city.toLowerCase().includes(q));
      const matchesType = selectedType === "all" || loc.type === selectedType;
      return matchesSearch && matchesType;
    });
  }, [locations, searchTerm, selectedType]);

  // Statistics
  const stats = useMemo(() => {
    const sourceCounts = locations.reduce((acc, loc) => {
      const src = loc.source || loc.type || "other";
      acc[src] = (acc[src] || 0) + 1;
      return acc;
    }, {});
    const polygons = locations.filter(l => l.polygon && l.polygon.length).length;
    const points = locations.length - polygons;
    return {
      total: locations.length,
      polygons,
      points,
      sources: sourceCounts,
      provider: searchProvider,
      mapplsConfigured: !!searchProvider,
    };
  }, [locations, searchProvider]);

  const handleSelectLocation = (location) => {
    setSelectedLocation(location);
    setMapCenter({ lat: location.lat, lng: location.lng });
  };

  const handleDeleteLocation = (id) => {
    if (window.confirm("Delete this location?")) {
      setLocations(locations.filter(l => l.id !== id));
      if (selectedLocation?.id === id) setSelectedLocation(null);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case "restaurant": return "Restaurant";
      case "address": return "Address";
      case "pickup": return "Pickup";
      case "drop": return "Drop";
      default: return "Pin";
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "restaurant": return theme.warning;
      case "address": return theme.primary;
      case "pickup": return theme.success;
      case "drop": return theme.error || theme.danger;
      default: return theme.textMuted;
    }
  };

  // Initialize Leaflet map
  useEffect(() => {
    if (!leafletMapRef.current || leafletMapInstance.current) return;

    try {
      const map = L.map(leafletMapRef.current, {
        center: [mapCenter.lat, mapCenter.lng],
        zoom: 6,
        zoomControl: true,
        scrollWheelZoom: true,
      });

      leafletTileRef.current = L.tileLayer(OSM_TILE_URL, {
        attribution: OSM_TILE_ATTR,
        maxZoom: 19,
      }).addTo(map);

      leafletMapInstance.current = map;

      map.on('click', async (e) => {
        if (activeTab !== 'reverse') return;
        const { lat, lng } = e.latlng;
        try {
          const data = await getData(`admin/settings/regions/reverse?lat=${lat.toFixed(6)}&lng=${lng.toFixed(6)}`);
          if (data?.status) {
            setSearchTerm(data.data?.display_name || `${lat.toFixed(4)}, ${lng.toFixed(4)}`);
          }
        } catch (_) {}
      });
    } catch (error) {
      console.error('Leaflet map init failed:', error);
    }

    return () => {
      try { leafletMapInstance.current?.remove?.(); } catch (_) {}
      leafletMapInstance.current = null;
      leafletMarkersRef.current = [];
      leafletPolygonsRef.current = [];
    };
  }, []);

  // Update markers when locations change
  useEffect(() => {
    const map = leafletMapInstance.current;
    if (!map) return;

    leafletMarkersRef.current.forEach(m => { try { m.remove(); } catch (_) {} });
    leafletPolygonsRef.current.forEach(p => { try { p.remove(); } catch (_) {} });
    leafletMarkersRef.current = [];
    leafletPolygonsRef.current = [];

    // Add polygons first
    locations.filter(loc => loc.polygon && loc.polygon.length >= 3).forEach(loc => {
      try {
        const color = loc.type === 'blacklisted' ? (theme.error || theme.danger) : (theme.success || '#22c55e');
        const poly = L.polygon(loc.polygon.map(p => [p.lat, p.lng]), {
          color, fillColor: color, fillOpacity: 0.15, weight: 2, opacity: 0.7,
        }).addTo(map);
        poly.bindPopup(`<strong>${loc.name}</strong><br/><small>${loc.address || ''}</small>`);
        leafletPolygonsRef.current.push(poly);
      } catch (_) {}
    });

    // Add markers for point results
    locations.filter(loc => !loc.polygon || loc.polygon.length < 3).forEach(loc => {
      try {
        const marker = L.marker([loc.lat, loc.lng]).addTo(map);
        marker.bindPopup(`<strong>${loc.name}</strong><br/><small>${loc.address || ''}</small>`);
        marker.on('click', () => setSelectedLocation(loc));
        leafletMarkersRef.current.push(marker);
      } catch (_) {}
    });

    // Fit bounds
    const allLayers = [...leafletMarkersRef.current, ...leafletPolygonsRef.current];
    if (allLayers.length > 0) {
      try {
        const group = new L.featureGroup(allLayers);
        map.fitBounds(group.getBounds().pad(0.1), { maxZoom: 14 });
      } catch (_) {}
    }
  }, [locations, theme]);

  // Pan to selected location
  useEffect(() => {
    if (selectedLocation && leafletMapInstance.current) {
      leafletMapInstance.current.setView([selectedLocation.lat, selectedLocation.lng], 14);
    }
  }, [selectedLocation]);

  // Update map center
  useEffect(() => {
    if (leafletMapInstance.current) {
      leafletMapInstance.current.setView([mapCenter.lat, mapCenter.lng], leafletMapInstance.current.getZoom() || 13);
    }
  }, [mapCenter]);

  // SUB-COMPONENTS
  const ServiceCard = ({ name, service, status, message }) => {
    const isOnline = status === "online";
    const color = isOnline ? theme.success : (theme.error || theme.danger);
    return (
      <div
        style={{
          background: theme.surface,
          border: `1px solid ${theme.border}`,
          borderRadius: "12px",
          padding: "16px",
          flex: 1,
          minWidth: "220px",
          transition: "all 0.2s ease",
          boxShadow: `0 2px 8px ${theme.primary}08`,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = `0 8px 20px ${color}15`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = `0 2px 8px ${theme.primary}08`;
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "10px",
              background: `${color}1a`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: color,
              flexShrink: 0,
            }}
          >
            {isOnline ? <CheckIcon /> : <XIcon />}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "14px", fontWeight: 700, color: theme.textPrimary, marginBottom: "2px" }}>
              {name}
            </div>
            <div style={{ fontSize: "11px", color: theme.textMuted, marginBottom: "6px" }}>
              {service}
            </div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                fontSize: "11px",
                color: color,
                background: `${color}15`,
                padding: "3px 8px",
                borderRadius: "10px",
                fontWeight: 500,
              }}
            >
              <span style={{ width: "5px", height: "5px", borderRadius: "50%", background: color }} />
              {message}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const DataCategory = ({ icon, label, count, isActive, onClick }) => (
    <button
      onClick={onClick}
      style={{
        padding: "10px 18px",
        background: isActive ? theme.primary : "transparent",
        color: isActive ? "#fff" : theme.textSecondary,
        border: isActive ? `1px solid ${theme.primary}` : `1px solid ${theme.border}`,
        borderRadius: "10px",
        cursor: "pointer",
        fontSize: "13px",
        fontWeight: 600,
        display: "flex",
        alignItems: "center",
        gap: "8px",
        transition: "all 0.2s ease",
        boxShadow: isActive ? `0 4px 12px ${theme.primary}40` : "none",
      }}
      onMouseEnter={(e) => {
        if (!isActive) {
          e.currentTarget.style.background = theme.surfaceLighter;
          e.currentTarget.style.color = theme.textPrimary;
          e.currentTarget.style.borderColor = theme.primary;
        }
      }}
      onMouseLeave={(e) => {
        if (!isActive) {
          e.currentTarget.style.background = "transparent";
          e.currentTarget.style.color = theme.textSecondary;
          e.currentTarget.style.borderColor = theme.border;
        }
      }}
    >
      <span style={{ fontSize: "15px" }}>{icon}</span>
      <span>{label}</span>
      <span
        style={{
          background: isActive ? "rgba(255,255,255,0.2)" : `${theme.primary}1a`,
          color: isActive ? "#fff" : theme.primary,
          padding: "2px 8px",
          borderRadius: "10px",
          fontSize: "11px",
          fontWeight: 700,
        }}
      >
        {count}
      </span>
    </button>
  );

  const LocationCard = ({ location, isSelected }) => {
    const hasPolygon = location.polygon && Array.isArray(location.polygon) && location.polygon.length > 0;
    const hasGeoJSON = location.geojson && Object.keys(location.geojson).length > 0;
    const sourceLabel = location.source || location.type || "unknown";
    return (
      <div
        onClick={() => handleSelectLocation(location)}
        style={{
          background: isSelected ? `${theme.primary}15` : theme.surface,
          border: `1px solid ${isSelected ? theme.primary : theme.border}`,
          borderRadius: "12px",
          padding: "14px",
          marginBottom: "10px",
          cursor: "pointer",
          transition: "all 0.2s ease",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "12px",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = theme.primary;
          e.currentTarget.style.boxShadow = `0 4px 12px ${theme.primary}20`;
          e.currentTarget.style.transform = "translateX(2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = isSelected ? theme.primary : theme.border;
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateX(0)";
        }}
      >
        <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: `${getTypeColor(location.type)}1a`, color: getTypeColor(location.type), display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", flexShrink: 0 }}>
          {getIcon(location.type)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: "13px", fontWeight: 600, color: theme.textPrimary, marginBottom: "4px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {location.name}
          </div>
          <div style={{ fontSize: "11px", color: theme.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {location.address}
          </div>
          <div style={{ fontSize: "10px", color: theme.textMuted, marginTop: "4px", fontFamily: "monospace", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <span>{location.lat.toFixed(4)}, {location.lng.toFixed(4)}</span>
            {hasPolygon && <span style={{ color: theme.success }}>polygon({location.polygon.length})</span>}
            {hasGeoJSON && <span style={{ color: theme.success }}>geoJSON</span>}
            <span style={{ background: `${theme.primary}15`, color: theme.primary, padding: "1px 6px", borderRadius: "6px", textTransform: "lowercase" }}>{sourceLabel}</span>
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleSelectLocation(location);
          }}
          style={{
            background: `${theme.primary}15`,
            border: `1px solid ${theme.primary}30`,
            cursor: "pointer",
            padding: "6px",
            borderRadius: "6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: theme.primary,
            transition: "all 0.2s ease",
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = theme.primary;
            e.currentTarget.style.color = "#fff";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = `${theme.primary}15`;
            e.currentTarget.style.color = theme.primary;
          }}
          title="Focus on map"
        >
          <MapPinIcon />
        </button>
      </div>
    );
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: theme.background,
        padding: "24px",
        fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        color: theme.textPrimary,
        transition: "background 0.3s ease",
      }}
    >
      <style>{`
        * { margin: 0; padding: 0; box-sizing: border-box; }
        button { transition: all 0.2s ease; cursor: pointer; }
        input:focus { outline: none; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: ${theme.surface}; }
        ::-webkit-scrollbar-thumb { background: ${theme.border}; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: ${theme.primary}; }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .leaflet-container { background: #e8e8e8 !important; }
      `}</style>

      {/* Header */}
      <div style={{ marginBottom: "24px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "20px", flexWrap: "wrap" }}>
          <div
            style={{
              width: "52px",
              height: "52px",
              borderRadius: "14px",
              background: `linear-gradient(135deg, ${theme.primary}, ${theme.primary}cc)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              boxShadow: `0 8px 20px ${theme.primary}40`,
            }}
          >
            <MapPinIcon />
          </div>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: "22px", fontWeight: 700, marginBottom: "4px", color: theme.textPrimary, display: "flex", alignItems: "center", gap: "8px" }}>
              Self-Hosted Maps
            </h1>
            <div style={{ fontSize: "12px", color: theme.textMuted, display: "flex", alignItems: "center", gap: "12px" }}>
              <span>OpenStreetMap + Nominatim</span>
              <span>|</span>
              <span>Free, no API key required</span>
            </div>
          </div>
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <div
              style={{
                padding: "6px 14px",
                background: `${theme.success}15`,
                border: `1px solid ${theme.success}30`,
                borderRadius: "20px",
                fontSize: "12px",
                color: theme.success,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: theme.success, animation: "pulse 2s infinite" }} />
              OSM Online
            </div>
          </div>
        </div>

        {/* Service Status */}
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <ServiceCard
            name="OpenStreetMap"
            service="Map Tiles & Search"
            status="online"
            message="Free tiles via tile.openstreetmap.org"
          />
          <ServiceCard
            name="Nominatim"
            service="Geocoding & Reverse"
            status="online"
            message="Search + reverse geocoding active"
          />
          <ServiceCard
            name="OSRM"
            service="Routing"
            status="online"
            message="Free routing via router.project-osrm.org"
          />
        </div>
      </div>

      {/* Data Section */}
      <div
        style={{
          background: theme.surface,
          border: `1px solid ${theme.border}`,
          borderRadius: "16px",
          padding: "20px",
          marginBottom: "24px",
          boxShadow: `0 4px 12px ${theme.primary}08`,
        }}
      >
        <div style={{ marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
          <div>
            <h2 style={{ fontSize: "17px", fontWeight: 700, marginBottom: "6px", color: theme.textPrimary, display: "flex", alignItems: "center", gap: "8px" }}>
              Map Data
            </h2>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <span style={{ fontSize: "12px", color: theme.textMuted, display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ color: theme.primary, fontWeight: 700 }}>{stats.total}</span> results
              </span>
              <span style={{ fontSize: "12px", color: theme.textMuted, display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ color: theme.primary, fontWeight: 700 }}>{stats.polygons}</span> with polygon
              </span>
              <span style={{ fontSize: "12px", color: theme.textMuted, display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ color: theme.primary, fontWeight: 700 }}>{stats.points}</span> point results
              </span>
              {stats.provider && (
                <span style={{ fontSize: "12px", color: theme.success, fontWeight: 600, display: "flex", alignItems: "center", gap: "4px" }}>
                  via {stats.provider}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Stats Chips */}
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "16px" }}>
          <span
            style={{
              background: `${theme.primary}15`,
              color: theme.primary,
              padding: "6px 12px",
              borderRadius: "20px",
              fontSize: "12px",
              fontWeight: 600,
              border: `1px solid ${theme.primary}30`,
            }}
          >
            Total results: {stats.total}
          </span>
          <span
            style={{
              background: `${theme.success}15`,
              color: theme.success,
              padding: "6px 12px",
              borderRadius: "20px",
              fontSize: "12px",
              fontWeight: 600,
              border: `1px solid ${theme.success}30`,
            }}
          >
            {stats.polygons} polygons
          </span>
          <span
            style={{
              background: `${theme.textMuted}15`,
              color: theme.textSecondary,
              padding: "6px 12px",
              borderRadius: "20px",
              fontSize: "12px",
              fontWeight: 600,
              border: `1px solid ${theme.border}`,
            }}
          >
            {stats.points} points
          </span>
          {stats.provider && (
            <span
              style={{
                background: `${theme.success}15`,
                color: theme.success,
                padding: "6px 12px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: 600,
                border: `1px solid ${theme.success}30`,
              }}
            >
              via {stats.provider}
            </span>
          )}
        </div>

        {/* Filter Controls */}
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "12px" }}>
          <div style={{ flex: 1, minWidth: "160px" }}>
            <label style={{ fontSize: "11px", color: theme.textMuted, marginBottom: "4px", display: "block", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 600 }}>Filter by type</label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              style={{
                width: "100%",
                padding: "9px 12px",
                background: theme.surfaceLighter,
                border: `1px solid ${theme.border}`,
                borderRadius: "10px",
                color: theme.textPrimary,
                fontSize: "13px",
                cursor: "pointer",
                outline: "none",
              }}
            >
              <option value="all">All types</option>
              <option value="official_ward">Official ward</option>
              <option value="city">Cities</option>
              <option value="place">Places</option>
              <option value="restaurant">Restaurants</option>
              <option value="boundary">Boundaries</option>
              <option value="allowed">Allowed zones</option>
              <option value="blacklisted">Blacklisted zones</option>
            </select>
          </div>
          <div style={{ flex: 1, minWidth: "160px" }}>
            <label style={{ fontSize: "11px", color: theme.textMuted, marginBottom: "4px", display: "block", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 600 }}>Filter by city</label>
            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              disabled={citiesLoading}
              style={{
                width: "100%",
                padding: "9px 12px",
                background: theme.surfaceLighter,
                border: `1px solid ${theme.border}`,
                borderRadius: "10px",
                color: theme.textPrimary,
                fontSize: "13px",
                cursor: citiesLoading ? "wait" : "pointer",
                outline: "none",
              }}
            >
              <option value="all">All cities</option>
              {availableCities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
              {citiesLoading && <option disabled>Loading...</option>}
            </select>
          </div>
          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <button
              onClick={() => {
                setSearchTerm("");
                setSelectedType("all");
                setSelectedState("all");
                setLocations([]);
                setSearchError(null);
              }}
              style={{
                padding: "9px 16px",
                background: `${theme.error || theme.danger}15`,
                border: `1px solid ${theme.error || theme.danger}30`,
                borderRadius: "10px",
                color: theme.error || theme.danger,
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = theme.error || theme.danger;
                e.currentTarget.style.color = "#fff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = `${theme.error || theme.danger}15`;
                e.currentTarget.style.color = theme.error || theme.danger;
              }}
            >
              <XIcon /> Clear
            </button>
          </div>
        </div>

        {/* Search Status */}
        {(searchLoading || searchError) && (
          <div
            style={{
              padding: "10px 14px",
              marginBottom: "12px",
              borderRadius: "10px",
              fontSize: "12px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              background: searchError ? `${theme.error || theme.danger}10` : `${theme.primary}10`,
              color: searchError ? theme.error || theme.danger : theme.primary,
              border: `1px solid ${searchError ? (theme.error || theme.danger) + "30" : theme.primary + "30"}`,
            }}
          >
            {searchLoading && (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: "pulse 1s infinite" }}>
                <circle cx="12" cy="12" r="10" strokeDasharray="32" strokeDashoffset="32" strokeLinecap="round" />
              </svg>
            )}
            <span>{searchLoading ? "Searching..." : searchError}</span>
          </div>
        )}

        {/* Data Categories */}
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
          <DataCategory
            icon="OpenStreetMap"
            label="OSM"
            count={stats.sources.OpenStreetMap || stats.sources.place || 0}
            isActive={selectedType === "place"}
            onClick={() => setSelectedType(selectedType === "place" ? "all" : "place")}
          />
          <DataCategory
            icon="Wards"
            label="Official Wards"
            count={stats.sources.official_ward || stats.sources.boundary || 0}
            isActive={selectedType === "official_ward"}
            onClick={() => setSelectedType(selectedType === "official_ward" ? "all" : "official_ward")}
          />
          <DataCategory
            icon="Cities"
            label="Cities"
            count={stats.sources.city || 0}
            isActive={selectedType === "city"}
            onClick={() => setSelectedType(selectedType === "city" ? "all" : "city")}
          />
          <DataCategory
            icon="Places"
            label="Places"
            count={stats.sources.place || 0}
            isActive={selectedType === "place"}
            onClick={() => setSelectedType(selectedType === "place" ? "all" : "place")}
          />
          <DataCategory
            icon="Restaurants"
            label="Restaurants"
            count={stats.sources.restaurant || 0}
            isActive={selectedType === "restaurant"}
            onClick={() => setSelectedType(selectedType === "restaurant" ? "all" : "restaurant")}
          />
          <div
            style={{
              marginLeft: "auto",
              fontSize: "12px",
              color: theme.textMuted,
              display: "flex",
              alignItems: "center",
              gap: "6px",
              background: theme.surfaceLighter,
              padding: "6px 14px",
              borderRadius: "20px",
              border: `1px solid ${theme.border}`,
            }}
          >
            <span style={{ color: theme.primary, fontWeight: 700 }}>{filteredLocations.length}</span> in view
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ display: "flex", gap: "24px", minHeight: "600px", flexWrap: "wrap" }}>
        {/* Left - Map (Leaflet OpenStreetMap) */}
        <div style={{ flex: 1, minWidth: "400px" }}>
          <div
            style={{
              background: theme.surface,
              border: `1px solid ${theme.border}`,
              borderRadius: "16px",
              overflow: "hidden",
              height: "100%",
              minHeight: "600px",
              display: "flex",
              flexDirection: "column",
              position: "relative",
              boxShadow: `0 4px 12px ${theme.primary}08`,
            }}
          >
            <div
              ref={leafletMapRef}
              id="zaptodoor-mapdashboard-map"
              style={{
                flex: 1,
                minHeight: '600px',
                background: '#e8e8e8',
                zIndex: 1,
              }}
            />
          </div>
        </div>

        {/* Right - Search & List */}
        <div style={{ flex: 1, minWidth: "340px", maxHeight: "650px", overflowY: "auto" }}>
          <div
            style={{
              background: theme.surface,
              border: `1px solid ${theme.border}`,
              borderRadius: "16px",
              padding: "20px",
              boxShadow: `0 4px 12px ${theme.primary}08`,
            }}
          >
            {/* Search Tabs */}
            <div
              style={{
                display: "flex",
                gap: "4px",
                marginBottom: "20px",
                padding: "4px",
                background: theme.surfaceLighter,
                borderRadius: "10px",
                border: `1px solid ${theme.border}`,
              }}
            >
              {[
                { id: "search", label: "Search", icon: <SearchIcon /> },
                { id: "route", label: "Route", icon: <RouteIcon /> },
                { id: "reverse", label: "Reverse", icon: <ReverseIcon /> },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    flex: 1,
                    padding: "10px 16px",
                    background: activeTab === tab.id ? theme.primary : "transparent",
                    color: activeTab === tab.id ? "#fff" : theme.textSecondary,
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (activeTab !== tab.id) {
                      e.currentTarget.style.background = theme.surface;
                      e.currentTarget.style.color = theme.textPrimary;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== tab.id) {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.color = theme.textSecondary;
                    }
                  }}
                >
                  {tab.icon} {tab.label}
                </button>
              ))}
            </div>

            {/* Search Input */}
            <div style={{ marginBottom: "16px" }}>
              <div style={{ position: "relative" }}>
                <div
                  style={{
                    position: "absolute",
                    left: "14px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: theme.primary,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <SearchIcon />
                </div>
                <input
                  type="text"
                  placeholder="Type a city / ward / place and press space"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px 90px 12px 42px",
                    background: theme.surfaceLighter,
                    border: `1px solid ${theme.border}`,
                    borderRadius: "10px",
                    fontSize: "13px",
                    color: theme.textPrimary,
                    transition: "all 0.2s ease",
                    boxSizing: "border-box",
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = theme.primary;
                    e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = theme.border;
                    e.currentTarget.style.boxShadow = "none";
                  }}
                />
                <div style={{ position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: "4px" }}>
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: theme.textMuted,
                        padding: "4px",
                        display: "flex",
                      }}
                    >
                      <XIcon />
                    </button>
                  )}
                  {searchLoading && (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={theme.primary} strokeWidth="2.5" style={{ animation: "pulse 1s infinite" }}>
                      <circle cx="12" cy="12" r="10" strokeDasharray="32" strokeDashoffset="32" strokeLinecap="round" />
                    </svg>
                  )}
                </div>
              </div>
              <div style={{ fontSize: "11px", color: theme.textMuted, marginTop: "6px", textAlign: "right" }}>
                {searchTerm.length < 2 ? "Type at least 2 characters" : `Live search${searchProvider ? ` via ${searchProvider}` : ""}`}
              </div>
            </div>

            {/* Search Message */}
            <div
              style={{
                padding: "12px",
                background: `${theme.primary}10`,
                border: `1px solid ${theme.primary}30`,
                borderRadius: "10px",
                fontSize: "12px",
                color: theme.primary,
                textAlign: "center",
                marginBottom: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
              }}
            >
              {searchTerm.length < 2 ? "Type at least 2 characters to search" : `Found ${filteredLocations.length} results`}
            </div>

            {/* Locations List */}
            <div>
              <h3
                style={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: theme.textMuted,
                  margin: "0 0 14px 0",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>Results</span>
                <span
                  style={{
                    background: `${theme.primary}1a`,
                    color: theme.primary,
                    padding: "2px 10px",
                    borderRadius: "10px",
                    fontSize: "11px",
                    fontWeight: 700,
                  }}
                >
                  {filteredLocations.length}
                </span>
              </h3>
              {filteredLocations.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 20px",
                    color: theme.textMuted,
                  }}
                >
                  <div
                    style={{
                      width: "60px",
                      height: "60px",
                      borderRadius: "50%",
                      background: `${theme.primary}15`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 16px",
                      fontSize: "24px",
                      color: theme.primary,
                    }}
                  >
                    <SearchIcon />
                  </div>
                  <p style={{ fontSize: "14px", fontWeight: 600, color: theme.textPrimary, marginBottom: "4px" }}>
                    No locations found
                  </p>
                  <p style={{ fontSize: "12px", margin: 0 }}>
                    Try adjusting your search or filters
                  </p>
                </div>
              ) : (
                filteredLocations.map((location) => (
                  <LocationCard
                    key={location.id}
                    location={location}
                    isSelected={selectedLocation?.id === location.id}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MappingDashboard;
